/**
 * Bundled fallback SVG HMI template (blast furnace).
 *
 * Used when the BE `GET /svg-templates` list is empty OR the remote `svgUrl`
 * fetch fails. Also serves as the reference contract for BE-authored templates:
 * every interactive region is a `<g class="hotspot" data-section="<id>">` and the
 * `sections` array below mirrors those ids with human labels.
 *
 * The same markup lives at /public/svg-templates/blastfurnace.svg so a real
 * `svgUrl` flow can be exercised in the mock layer.
 */

export const BLASTFURNACE_TEMPLATE_ID = 'tmpl-blastfurnace';

export const BLASTFURNACE_SECTIONS = [
  { id: 'furnace_top', label: 'Furnace Top' },
  { id: 'stack', label: 'Stack' },
  { id: 'bosh', label: 'Bosh' },
  { id: 'tuyeres', label: 'Tuyeres' },
  { id: 'hearth', label: 'Hearth' },
  { id: 'hot_blast_stove', label: 'Hot Blast Stove' },
  { id: 'cold_blast', label: 'Cold Blast Blower' },
  { id: 'stock_house', label: 'Stock House' },
  { id: 'slag', label: 'Slag Granulation' },
];

// Inlined markup — kept byte-identical to public/svg-templates/blastfurnace.svg.
export const BLASTFURNACE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 560" width="800" height="560" font-family="Inter, system-ui, sans-serif">
  <defs>
    <linearGradient id="bf-steel" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="var(--hmi-steel-a, #cbd5e1)"/>
      <stop offset="0.5" stop-color="var(--hmi-steel-b, #f1f5f9)"/>
      <stop offset="1" stop-color="var(--hmi-steel-c, #94a3b8)"/>
    </linearGradient>
    <linearGradient id="bf-heat" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0" stop-color="#7f1d1d"/>
      <stop offset="0.5" stop-color="#ea580c"/>
      <stop offset="1" stop-color="#facc15"/>
    </linearGradient>
    <linearGradient id="bf-stove" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="var(--hmi-steel-b, #e2e8f0)"/>
      <stop offset="1" stop-color="var(--hmi-steel-c, #94a3b8)"/>
    </linearGradient>
    <linearGradient id="bf-pipe" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#a5b4fc"/>
      <stop offset="1" stop-color="#6366f1"/>
    </linearGradient>
    <style>
      svg { --hmi-label: #334155; --hmi-label-sm: #64748b; --hmi-stroke: #64748b; --hmi-stroke-soft: #94a3b8; --hmi-ground: #e2e8f0; }
      .lbl { fill: var(--hmi-label); font-size:12px; font-weight:600; }
      .lbl-sm { fill: var(--hmi-label-sm); font-size:10px; font-weight:500; }
      .lbl-hot { fill:#fff7ed; font-size:12px; font-weight:600; }
      .lbl-hot-sm { fill:#fff7ed; font-size:10px; font-weight:500; }
      .hotspot { cursor:pointer; transition:opacity .15s; }
    </style>
  </defs>

  <rect x="0" y="520" width="800" height="40" fill="var(--hmi-ground)"/>
  <line x1="0" y1="520" x2="800" y2="520" stroke="var(--hmi-stroke-soft)" stroke-width="2"/>

  <g class="hotspot" data-section="furnace_top">
    <rect x="318" y="40" width="84" height="44" rx="6" fill="url(#bf-steel)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <rect x="344" y="18" width="32" height="28" rx="3" fill="var(--hmi-steel-a, #cbd5e1)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <path d="M330 84 Q300 110 270 130 L290 150 Q330 120 350 100 Z" fill="url(#bf-pipe)" stroke="#4f46e5" stroke-width="1.5"/>
    <path d="M390 84 Q420 110 450 130 L430 150 Q390 120 370 100 Z" fill="url(#bf-pipe)" stroke="#4f46e5" stroke-width="1.5"/>
    <text x="360" y="14" text-anchor="middle" class="lbl">Furnace Top</text>
  </g>

  <g class="hotspot" data-section="stack">
    <path d="M325 84 L395 84 L420 230 L300 230 Z" fill="url(#bf-steel)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <line x1="308" y1="120" x2="412" y2="120" stroke="var(--hmi-stroke-soft)" stroke-width="1.5"/>
    <line x1="304" y1="160" x2="416" y2="160" stroke="var(--hmi-stroke-soft)" stroke-width="1.5"/>
    <line x1="301" y1="200" x2="419" y2="200" stroke="var(--hmi-stroke-soft)" stroke-width="1.5"/>
    <text x="360" y="200" text-anchor="middle" class="lbl">Stack</text>
  </g>

  <g class="hotspot" data-section="bosh">
    <path d="M300 230 L420 230 L405 320 L315 320 Z" fill="url(#bf-heat)" stroke="#9a3412" stroke-width="2"/>
    <text x="360" y="280" text-anchor="middle" class="lbl-hot">Bosh</text>
  </g>

  <g class="hotspot" data-section="tuyeres">
    <rect x="315" y="320" width="90" height="34" fill="url(#bf-heat)" stroke="#9a3412" stroke-width="2"/>
    <circle cx="315" cy="337" r="7" fill="#fde68a" stroke="#b45309" stroke-width="1.5"/>
    <circle cx="405" cy="337" r="7" fill="#fde68a" stroke="#b45309" stroke-width="1.5"/>
    <rect x="285" y="332" width="30" height="10" fill="url(#bf-pipe)" stroke="#4f46e5" stroke-width="1"/>
    <rect x="405" y="332" width="30" height="10" fill="url(#bf-pipe)" stroke="#4f46e5" stroke-width="1"/>
    <text x="360" y="343" text-anchor="middle" class="lbl-hot-sm">Tuyeres</text>
  </g>

  <g class="hotspot" data-section="hearth">
    <path d="M315 354 L405 354 L398 430 L322 430 Z" fill="url(#bf-heat)" stroke="#7f1d1d" stroke-width="2"/>
    <rect x="322" y="430" width="76" height="20" rx="3" fill="#78350f" stroke="#451a03" stroke-width="2"/>
    <path d="M398 415 L470 440 L470 452 L396 428 Z" fill="#ea580c" stroke="#9a3412" stroke-width="1.5"/>
    <text x="360" y="395" text-anchor="middle" class="lbl-hot">Hearth</text>
    <text x="475" y="448" class="lbl-sm">Tap Hole</text>
  </g>

  <g class="hotspot" data-section="hot_blast_stove">
    <rect x="120" y="250" width="90" height="200" rx="10" fill="url(#bf-stove)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <ellipse cx="165" cy="250" rx="45" ry="14" fill="var(--hmi-steel-b, #e2e8f0)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <path d="M120 270 Q165 240 210 270" fill="none" stroke="var(--hmi-stroke-soft)" stroke-width="1.5"/>
    <path d="M210 350 L285 340" fill="none" stroke="#6366f1" stroke-width="8" stroke-linecap="round"/>
    <text x="165" y="360" text-anchor="middle" class="lbl">Hot Blast</text>
    <text x="165" y="376" text-anchor="middle" class="lbl">Stove</text>
  </g>

  <g class="hotspot" data-section="cold_blast">
    <rect x="40" y="380" width="64" height="70" rx="8" fill="url(#bf-steel)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <circle cx="72" cy="415" r="18" fill="#e0e7ff" stroke="#6366f1" stroke-width="2"/>
    <path d="M72 401 L72 415 L84 421" fill="none" stroke="#4f46e5" stroke-width="2"/>
    <path d="M104 410 L120 400" fill="none" stroke="#6366f1" stroke-width="6" stroke-linecap="round"/>
    <text x="72" y="468" text-anchor="middle" class="lbl-sm">Cold Blast Blower</text>
  </g>

  <g class="hotspot" data-section="stock_house">
    <rect x="560" y="120" width="170" height="90" rx="8" fill="url(#bf-steel)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <path d="M560 210 L420 100" fill="none" stroke="var(--hmi-stroke-soft)" stroke-width="6"/>
    <rect x="575" y="140" width="44" height="50" rx="3" fill="var(--hmi-steel-a, #cbd5e1)" stroke="var(--hmi-stroke)" stroke-width="1.5"/>
    <rect x="635" y="140" width="44" height="50" rx="3" fill="var(--hmi-steel-a, #cbd5e1)" stroke="var(--hmi-stroke)" stroke-width="1.5"/>
    <text x="645" y="112" text-anchor="middle" class="lbl">Stock House</text>
    <text x="597" y="170" text-anchor="middle" class="lbl-sm">Ore</text>
    <text x="657" y="170" text-anchor="middle" class="lbl-sm">Coke</text>
  </g>

  <g class="hotspot" data-section="slag">
    <rect x="560" y="420" width="120" height="70" rx="8" fill="url(#bf-steel)" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <path d="M470 446 L560 455" fill="none" stroke="#ea580c" stroke-width="4"/>
    <ellipse cx="620" cy="455" rx="36" ry="12" fill="#bae6fd" stroke="#0284c7" stroke-width="1.5"/>
    <text x="620" y="412" text-anchor="middle" class="lbl">Slag Granulation</text>
  </g>
</svg>`;

export const BLASTFURNACE_TEMPLATE = {
  id: BLASTFURNACE_TEMPLATE_ID,
  name: 'Blast Furnace (Schematic)',
  svgUrl: '/svg-templates/blastfurnace.svg',
  sections: BLASTFURNACE_SECTIONS,
};

// ── Blast Furnace v2 (detailed plant HMI) — the default bundled template ───────

export const BLASTFURNACE_V2_TEMPLATE_ID = 'tmpl-blastfurnace-v2';

export const BLASTFURNACE_V2_SECTIONS = [
  { id: 'stockhouse', label: 'Stockhouse & Burden Prep' },
  { id: 'top_charge', label: 'Bell-less Top / Charging' },
  { id: 'furnace_shaft', label: 'Furnace Shaft & Bosh' },
  { id: 'tuyeres', label: 'Tuyeres & Raceway' },
  { id: 'casthouse', label: 'Hearth & Casthouse' },
  { id: 'hot_blast_stoves', label: 'Hot Blast Stoves & Blower' },
  { id: 'pci_system', label: 'PCI System' },
  { id: 'gas_cleaning_trt', label: 'Gas Cleaning & TRT' },
];

// Inlined markup — kept byte-identical to public/svg-templates/blastfurnace_v2.svg.
export const BLASTFURNACE_V2_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1400 700" width="1400" height="700" font-family="Inter, system-ui, sans-serif">
  <defs>
    <!-- Structural steel (theme-aware): app injects dark steel values in dark mode -->
    <linearGradient id="bf2-steel" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="var(--hmi-steel-b, #f1f5f9)"/>
      <stop offset="1" stop-color="var(--hmi-steel-a, #d4dde7)"/>
    </linearGradient>
    <!-- Thermal profile inside the furnace (meaningful colors, kept hardcoded) -->
    <linearGradient id="bf2-shaft" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#475569"/>
      <stop offset="0.35" stop-color="#f59e0b"/>
      <stop offset="0.65" stop-color="#ef4444"/>
      <stop offset="1" stop-color="#7f1d1d"/>
    </linearGradient>
    <linearGradient id="bf2-tuyere" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#f59e0b"/>
      <stop offset="1" stop-color="#78350f"/>
    </linearGradient>
    <linearGradient id="bf2-hearth" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#991b1b"/>
      <stop offset="1" stop-color="#7f1d1d"/>
    </linearGradient>
    <radialGradient id="bf2-hmglow" cx="0.5" cy="0.5" r="0.5">
      <stop offset="0" stop-color="#fcd34d" stop-opacity="0.95"/>
      <stop offset="0.5" stop-color="#f59e0b" stop-opacity="0.6"/>
      <stop offset="1" stop-color="#7f1d1d" stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="bf2-stack" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="var(--hmi-steel-c, #94a3b8)"/>
      <stop offset="0.5" stop-color="var(--hmi-steel-b, #f1f5f9)"/>
      <stop offset="1" stop-color="var(--hmi-steel-c, #94a3b8)"/>
    </linearGradient>
    <clipPath id="bf2-inside">
      <path d="M 318 168 L 442 168 L 442 197 C 453 222, 499 284, 506 332 L 506 373 C 505 400, 462 426, 438 442 L 438 466 L 322 466 L 322 442 C 298 426, 255 400, 254 373 L 254 332 C 261 284, 307 222, 318 197 Z"/>
    </clipPath>
    <style>
      svg {
        --hmi-label: #334155;        /* primary label text — AAA (>=7:1) on page + steel fills */
        --hmi-label-sm: #475569;     /* secondary label text — AAA on the page background only */
        --hmi-stroke: #64748b;       /* main outlines (non-text, >=3:1) */
        --hmi-stroke-soft: #94a3b8;  /* faint lines */
        --hmi-ground: #e2e8f0;       /* ground / base plate */
        --hmi-steel-a: #d4dde7;      /* steel gradient stops (light enough for AAA label text) */
        --hmi-steel-b: #f1f5f9;
        --hmi-steel-c: #94a3b8;
      }
      .lbl       { fill: var(--hmi-label); font-size:11px; font-weight:600; }
      .lbl-sm    { fill: var(--hmi-label-sm); font-size:9px; font-weight:500; }   /* page background only */
      .lbl-title { fill: var(--hmi-label); font-size:11px; font-weight:700; letter-spacing:0.4px; }
      .lbl-head  { fill: var(--hmi-label); font-size:11px; font-weight:600; letter-spacing:1.5px; }
      .lbl-in    { fill: var(--hmi-label); font-size:9px; font-weight:500; }       /* small text on box fills — uses AAA primary color */
      .lbl-hot   { fill:#fff7ed; font-size:11px; font-weight:600; }                /* on solid hot/dark fills */
      .lbl-hot-sm{ fill:#fde68a; font-size:8px; font-weight:600; }
      .zlbl      { fill:#f8fafc; font-weight:700; }                                /* furnace zone-tag text (on solid dark chips) */
      .hotspot   { cursor:pointer; transition:opacity .15s; }
    </style>
  </defs>

  <!-- Stage headers (on page background) -->
  <text x="120" y="32" text-anchor="middle" class="lbl-head">RAW MATERIAL</text>
  <text x="380" y="32" text-anchor="middle" class="lbl-head">FURNACE</text>
  <text x="695" y="32" text-anchor="middle" class="lbl-head">HOT BLAST &amp; FUEL INJECTION</text>
  <text x="1025" y="32" text-anchor="middle" class="lbl-head">GAS &amp; ENERGY RECOVERY</text>

  <!-- ============ Flow lines (decoration, drawn behind equipment) ============ -->
  <g fill="none" stroke-linecap="round" opacity="0.9">
    <path d="M 220 130 L 260 130 L 260 90 L 290 90" stroke="var(--hmi-stroke)" stroke-width="2.2"/>
    <path d="M 380 160 L 380 240" stroke="var(--hmi-stroke)" stroke-width="1.4" opacity="0.4"/>
    <path d="M 580 230 L 540 230 L 540 490 L 490 510" stroke="#f59e0b" stroke-width="2.6"/>
    <path d="M 580 520 L 555 520 L 555 510 L 490 510" stroke="#92400e" stroke-width="1.8"/>
    <path d="M 525 600 L 540 612" stroke="#dc2626" stroke-width="2.2"/>
    <path d="M 220 605 L 215 615" stroke="#a78bfa" stroke-width="2"/>
    <path d="M 470 92 L 545 58 L 852 58 L 905 196" stroke="#7c3aed" stroke-width="2.4"/>
    <path d="M 1095 263 L 1095 400 L 870 400 L 810 380" stroke="#06b6d4" stroke-width="1.8"/>
    <path d="M 1190 230 L 1235 230" stroke="#06b6d4" stroke-width="1.8"/>
    <path d="M 1095 198 L 1095 130 L 1190 130" stroke="#10b981" stroke-width="1.6"/>
  </g>
  <!-- Flow labels use the neutral page-text color for AAA; the adjacent colored line + legend carry the category color (1.4.1: color is not the only cue). -->
  <text x="234" y="120" class="lbl-sm" style="font-size:7.5px">burden</text>
  <text x="500" y="70" class="lbl-sm" style="font-size:8px;font-weight:700">top gas &#8594; cleaning</text>
  <text x="558" y="478" class="lbl-sm" style="font-size:7.5px;font-weight:700">hot blast</text>
  <text x="524" y="503" class="lbl-sm" style="font-size:7.5px;font-weight:700">PCI coal</text>
  <text x="930" y="394" class="lbl-sm" style="font-size:7.5px;font-weight:700">clean BF gas &#8594; stoves</text>
  <text x="1100" y="123" class="lbl-sm" style="font-size:7.5px;font-weight:700">TRT power</text>

  <!-- ============ Stockhouse & burden prep ============ -->
  <g class="hotspot" data-section="stockhouse">
    <rect x="20" y="70" width="200" height="160" rx="8" fill="url(#bf2-steel)" stroke="var(--hmi-stroke)" stroke-width="1.6"/>
    <text x="120" y="90" text-anchor="middle" class="lbl-title">STOCKHOUSE &amp; BURDEN PREP</text>
    <rect x="36" y="100" width="42" height="78" fill="#64748b" fill-opacity="0.4" stroke="var(--hmi-stroke-soft)" stroke-width="0.8"/>
    <rect x="84" y="100" width="42" height="78" fill="#334155" fill-opacity="0.45" stroke="var(--hmi-stroke-soft)" stroke-width="0.8"/>
    <rect x="132" y="100" width="42" height="78" fill="#92400e" fill-opacity="0.5" stroke="var(--hmi-stroke-soft)" stroke-width="0.8"/>
    <rect x="180" y="100" width="22" height="78" fill="#0891b2" fill-opacity="0.35" stroke="var(--hmi-stroke-soft)" stroke-width="0.8"/>
    <text x="57" y="194" text-anchor="middle" class="lbl-in" style="font-size:8px">SINTER</text>
    <text x="105" y="194" text-anchor="middle" class="lbl-in" style="font-size:8px">PELLET</text>
    <text x="153" y="194" text-anchor="middle" class="lbl-in" style="font-size:8px">COKE</text>
    <text x="191" y="194" text-anchor="middle" class="lbl-in" style="font-size:7.5px">FLUX</text>
    <text x="120" y="216" text-anchor="middle" class="lbl-in">Coke CSR &#183; Sinter/Pellet ratio</text>
  </g>

  <!-- ============ Bell-less top (top charge) ============ -->
  <g class="hotspot" data-section="top_charge">
    <path d="M 290 80 L 470 80 L 450 160 L 310 160 Z" fill="url(#bf2-steel)" stroke="var(--hmi-stroke)" stroke-width="1.6"/>
    <rect x="350" y="58" width="60" height="22" rx="3" fill="#64748b" fill-opacity="0.5" stroke="var(--hmi-stroke)" stroke-width="1"/>
    <line x1="380" y1="82" x2="380" y2="158" stroke="var(--hmi-stroke-soft)" stroke-width="1.4" stroke-dasharray="3,3"/>
    <path d="M 380 110 L 408 130" fill="none" stroke="var(--hmi-stroke)" stroke-width="2"/>
    <circle cx="380" cy="110" r="3" fill="var(--hmi-stroke)"/>
    <text x="380" y="53" text-anchor="middle" class="lbl-sm" style="font-size:7.5px">CHARGE BIN</text>
    <text x="380" y="108" text-anchor="middle" class="lbl-title" style="font-size:9px">BELL-LESS TOP</text>
    <text x="380" y="126" text-anchor="middle" class="lbl-in">Top Pressure &#183; &#951;_CO &#183; Top Temp</text>
  </g>

  <!-- ============ Furnace shaft & bosh ============ -->
  <g class="hotspot" data-section="furnace_shaft">
    <path d="M 310 160 L 450 160 L 450 195 C 462 220, 508 282, 515 330 L 515 375 C 514 402, 470 430, 445 445 L 445 472 L 315 472 L 315 445 C 290 430, 246 402, 245 375 L 245 330 C 252 282, 298 220, 310 195 Z" fill="url(#bf2-shaft)" stroke="var(--hmi-stroke)" stroke-width="2.2"/>
    <path d="M 318 168 L 442 168 L 442 197 C 453 222, 499 284, 506 332 L 506 373 C 505 400, 462 426, 438 442 L 438 466 L 322 466 L 322 442 C 298 426, 255 400, 254 373 L 254 332 C 261 284, 307 222, 318 197 Z" fill="none" stroke="#e2e8f0" stroke-width="0.7" stroke-opacity="0.4" stroke-dasharray="2,3" pointer-events="none"/>
    <g pointer-events="none" clip-path="url(#bf2-inside)">
      <rect x="250" y="170" width="260" height="5" fill="#475569" fill-opacity="0.55"/>
      <rect x="250" y="177" width="260" height="4" fill="#7c2d12" fill-opacity="0.55"/>
      <rect x="250" y="184" width="260" height="5" fill="#475569" fill-opacity="0.55"/>
      <rect x="250" y="192" width="260" height="5" fill="#7c2d12" fill-opacity="0.55"/>
      <rect x="250" y="201" width="260" height="5" fill="#475569" fill-opacity="0.55"/>
      <rect x="250" y="210" width="260" height="6" fill="#7c2d12" fill-opacity="0.55"/>
      <rect x="250" y="220" width="260" height="6" fill="#475569" fill-opacity="0.55"/>
      <rect x="250" y="232" width="260" height="6" fill="#7c2d12" fill-opacity="0.55"/>
      <rect x="250" y="244" width="260" height="7" fill="#475569" fill-opacity="0.55"/>
      <rect x="250" y="257" width="260" height="7" fill="#7c2d12" fill-opacity="0.55"/>
      <rect x="250" y="270" width="260" height="7" fill="#475569" fill-opacity="0.55"/>
    </g>
    <g pointer-events="none">
      <line x1="310" y1="195" x2="450" y2="195" stroke="var(--hmi-stroke-soft)" stroke-width="0.7" stroke-opacity="0.5"/>
      <line x1="245" y1="375" x2="515" y2="375" stroke="var(--hmi-stroke-soft)" stroke-width="0.7" stroke-opacity="0.4"/>
      <!-- Zone tags: solid dark chips guarantee AAA text contrast over the hot gradient -->
      <rect x="350" y="177" width="60" height="10" rx="3" fill="#1e293b"/>
      <text x="380" y="184" text-anchor="middle" class="zlbl" style="font-size:6.5px;letter-spacing:0.4px">STOCKLINE</text>
      <rect x="333" y="285" width="94" height="12" rx="4" fill="#422006"/>
      <text x="380" y="293" text-anchor="middle" fill="#fde68a" style="font-size:7.5px;font-weight:700">GRANULAR ZONE</text>
      <rect x="287" y="308" width="186" height="13" rx="5" fill="#7c2d12"/>
      <text x="380" y="317" text-anchor="middle" fill="#fde68a" style="font-size:7.5px;font-weight:700">COHESIVE ZONE</text>
      <rect x="261" y="349" width="238" height="14" rx="6" fill="#7f1d1d"/>
      <text x="380" y="359" text-anchor="middle" fill="#fee2e2" style="font-size:7.5px;font-weight:700">ACTIVE / DRIPPING</text>
      <rect x="299" y="420" width="162" height="14" rx="6" fill="#5b1414"/>
      <text x="380" y="430" text-anchor="middle" fill="#fcd34d" style="font-size:7.5px;font-weight:700">HEARTH (HM POOL)</text>
      <rect x="441" y="461" width="6" height="6" fill="#fcd34d" stroke="#b45309" stroke-width="0.5"/>
      <ellipse cx="450" cy="464" rx="5" ry="2.5" fill="#f59e0b"/>
    </g>
    <text x="380" y="498" text-anchor="middle" class="lbl-title" style="font-size:9px">FURNACE SHAFT &amp; BOSH</text>
  </g>

  <!-- ============ Tuyeres / raceway ============ -->
  <g class="hotspot" data-section="tuyeres">
    <rect x="270" y="455" width="220" height="85" rx="4" fill="#ffffff" fill-opacity="0"/>
    <rect x="270" y="510" width="220" height="14" rx="4" fill="url(#bf2-tuyere)" stroke="#b45309" stroke-width="1.2" pointer-events="none"/>
    <g pointer-events="none">
      <circle cx="295" cy="478" r="3.5" fill="#fcd34d" stroke="#b45309" stroke-width="0.7"/>
      <line x1="295" y1="481" x2="295" y2="510" stroke="#f59e0b" stroke-width="1.4" stroke-opacity="0.7"/>
      <circle cx="327" cy="478" r="3.5" fill="#fcd34d" stroke="#b45309" stroke-width="0.7"/>
      <line x1="327" y1="481" x2="327" y2="510" stroke="#f59e0b" stroke-width="1.4" stroke-opacity="0.7"/>
      <circle cx="359" cy="478" r="3.5" fill="#fcd34d" stroke="#b45309" stroke-width="0.7"/>
      <line x1="359" y1="481" x2="359" y2="510" stroke="#f59e0b" stroke-width="1.4" stroke-opacity="0.7"/>
      <circle cx="391" cy="478" r="3.5" fill="#fcd34d" stroke="#b45309" stroke-width="0.7"/>
      <line x1="391" y1="481" x2="391" y2="510" stroke="#f59e0b" stroke-width="1.4" stroke-opacity="0.7"/>
      <circle cx="423" cy="478" r="3.5" fill="#fcd34d" stroke="#b45309" stroke-width="0.7"/>
      <line x1="423" y1="481" x2="423" y2="510" stroke="#f59e0b" stroke-width="1.4" stroke-opacity="0.7"/>
      <circle cx="455" cy="478" r="3.5" fill="#fcd34d" stroke="#b45309" stroke-width="0.7"/>
      <line x1="455" y1="481" x2="455" y2="510" stroke="#f59e0b" stroke-width="1.4" stroke-opacity="0.7"/>
    </g>
    <text x="380" y="538" text-anchor="middle" class="lbl-title" style="font-size:9px">TUYERES &#183; RACEWAY &#183; O&#8322; ENRICHMENT</text>
  </g>

  <!-- ============ Hearth & casthouse ============ -->
  <g class="hotspot" data-section="casthouse">
    <rect x="180" y="555" width="380" height="95" rx="8" fill="url(#bf2-hearth)" stroke="var(--hmi-stroke)" stroke-width="1.6"/>
    <g pointer-events="none">
      <path d="M 410 568 L 470 580 L 525 600" stroke="#f87171" stroke-width="4" fill="none" stroke-opacity="0.95"/>
      <path d="M 350 568 L 280 580 L 220 605" stroke="#c4b5fd" stroke-width="3" fill="none" stroke-opacity="0.95"/>
      <ellipse cx="540" cy="612" rx="18" ry="9" fill="#dc2626" fill-opacity="0.75" stroke="#fca5a5" stroke-width="1"/>
      <ellipse cx="540" cy="608" rx="14" ry="3" fill="#fcd34d" fill-opacity="0.9"/>
      <text x="540" y="635" text-anchor="middle" class="lbl-hot-sm" style="font-size:7px">TORPEDO</text>
      <path d="M 195 615 L 235 615 L 230 632 L 200 632 Z" fill="#a78bfa" fill-opacity="0.6" stroke="#c4b5fd" stroke-width="1"/>
      <text x="215" y="648" text-anchor="middle" class="lbl-hot-sm" style="font-size:7px">SLAG POT</text>
      <ellipse cx="370" cy="566" rx="60" ry="6" fill="url(#bf2-hmglow)" fill-opacity="0.85"/>
    </g>
    <text x="370" y="591" text-anchor="middle" class="lbl-hot">HEARTH &amp; CASTHOUSE</text>
    <text x="370" y="608" text-anchor="middle" class="lbl-hot-sm" style="font-size:9px">Hot Metal C &#183; Si &#183; S</text>
    <text x="370" y="624" text-anchor="middle" class="lbl-hot-sm">Tap Duration &#183; Slag Rate &#183; Cast Duration</text>
  </g>

  <!-- ============ Hot blast stoves & blower ============ -->
  <g class="hotspot" data-section="hot_blast_stoves">
    <rect x="580" y="80" width="230" height="360" rx="10" fill="#f59e0b" fill-opacity="0.05" stroke="var(--hmi-stroke)" stroke-width="1"/>
    <text x="695" y="100" text-anchor="middle" class="lbl-title">HOT BLAST STOVES &amp; BLOWER</text>
    <g pointer-events="none">
      <rect x="594" y="120" width="52" height="220" rx="7" fill="#f59e0b" fill-opacity="0.3" stroke="var(--hmi-stroke)" stroke-width="1.4"/>
      <ellipse cx="620" cy="120" rx="26" ry="7" fill="#f59e0b" fill-opacity="0.45" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <ellipse cx="620" cy="340" rx="26" ry="7" fill="#78350f" fill-opacity="0.55" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <circle cx="620" cy="220" r="14" fill="#fcd34d" fill-opacity="0.4"/>
      <text x="620" y="232" text-anchor="middle" class="lbl-in" style="font-weight:700">S1</text>
      <text x="620" y="248" text-anchor="middle" class="lbl-in" style="font-size:7.5px">BLAST</text>
      <rect x="664" y="120" width="52" height="220" rx="7" fill="#a78bfa" fill-opacity="0.22" stroke="var(--hmi-stroke)" stroke-width="1.4"/>
      <ellipse cx="690" cy="120" rx="26" ry="7" fill="#f59e0b" fill-opacity="0.45" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <ellipse cx="690" cy="340" rx="26" ry="7" fill="#78350f" fill-opacity="0.55" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <text x="690" y="232" text-anchor="middle" class="lbl-in" style="font-weight:700">S2</text>
      <text x="690" y="248" text-anchor="middle" class="lbl-in" style="font-size:7.5px">GAS</text>
      <rect x="734" y="120" width="52" height="220" rx="7" fill="#64748b" fill-opacity="0.18" stroke="var(--hmi-stroke)" stroke-width="1.4"/>
      <ellipse cx="760" cy="120" rx="26" ry="7" fill="#f59e0b" fill-opacity="0.45" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <ellipse cx="760" cy="340" rx="26" ry="7" fill="#78350f" fill-opacity="0.55" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <text x="760" y="232" text-anchor="middle" class="lbl-in" style="font-weight:700">S3</text>
      <text x="760" y="248" text-anchor="middle" class="lbl-in" style="font-size:7.5px">IDLE</text>
    </g>
    <text x="695" y="397" text-anchor="middle" class="lbl-in">Hot Blast Temp &#183; Blast Volume</text>
    <text x="695" y="413" text-anchor="middle" class="lbl-in">Stove Health &#183; Blower Availability</text>
    <text x="695" y="429" text-anchor="middle" class="lbl-in">HB Pressure &#183; Stove Cycle</text>
  </g>

  <!-- ============ PCI system ============ -->
  <g class="hotspot" data-section="pci_system">
    <rect x="580" y="470" width="230" height="120" rx="8" fill="#92400e" fill-opacity="0.14" stroke="var(--hmi-stroke)" stroke-width="1.5"/>
    <text x="695" y="490" text-anchor="middle" class="lbl-title">PCI SYSTEM</text>
    <g pointer-events="none">
      <circle cx="615" cy="540" r="22" fill="#92400e" fill-opacity="0.2" stroke="var(--hmi-stroke)" stroke-width="1.3"/>
      <line x1="600" y1="540" x2="630" y2="540" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <line x1="615" y1="525" x2="615" y2="555" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <text x="615" y="572" text-anchor="middle" class="lbl-in" style="font-size:7px">COAL MILL</text>
      <line x1="648" y1="530" x2="684" y2="530" stroke="#92400e" stroke-width="2"/>
      <line x1="648" y1="538" x2="684" y2="538" stroke="#92400e" stroke-width="2"/>
      <line x1="648" y1="546" x2="684" y2="546" stroke="#92400e" stroke-width="2"/>
      <text x="666" y="560" text-anchor="middle" class="lbl-in" style="font-size:6.5px">LANCES</text>
    </g>
    <text x="802" y="512" text-anchor="end" class="lbl-in" style="font-size:8px">PCI Rate &#183; Coal Fineness</text>
    <text x="802" y="528" text-anchor="end" class="lbl-in" style="font-size:8px">Mill Vibration &#183; Health</text>
    <text x="802" y="544" text-anchor="end" class="lbl-in" style="font-size:8px">30d Trips</text>
  </g>

  <!-- ============ BF gas cleaning & TRT ============ -->
  <g class="hotspot" data-section="gas_cleaning_trt">
    <rect x="860" y="160" width="330" height="170" rx="8" fill="#7c3aed" fill-opacity="0.1" stroke="var(--hmi-stroke)" stroke-width="1.5"/>
    <text x="1025" y="180" text-anchor="middle" class="lbl-title">BF GAS CLEANING &amp; TRT</text>
    <g pointer-events="none">
      <path d="M 905 200 L 945 200 L 935 245 L 945 255 L 905 255 L 915 245 Z" fill="#64748b" fill-opacity="0.28" stroke="var(--hmi-stroke)" stroke-width="1.2"/>
      <text x="925" y="226" text-anchor="middle" class="lbl-in" style="font-size:7px;font-weight:700">DUST</text>
      <text x="925" y="237" text-anchor="middle" class="lbl-in" style="font-size:7px;font-weight:700">CATCHER</text>
      <rect x="975" y="200" width="60" height="60" rx="4" fill="#a78bfa" fill-opacity="0.22" stroke="var(--hmi-stroke)" stroke-width="1.2"/>
      <line x1="985" y1="218" x2="1025" y2="218" stroke="var(--hmi-stroke-soft)" stroke-width="0.5" stroke-opacity="0.6"/>
      <line x1="985" y1="232" x2="1025" y2="232" stroke="var(--hmi-stroke-soft)" stroke-width="0.5" stroke-opacity="0.6"/>
      <line x1="985" y1="246" x2="1025" y2="246" stroke="var(--hmi-stroke-soft)" stroke-width="0.5" stroke-opacity="0.6"/>
      <text x="1005" y="277" text-anchor="middle" class="lbl-in" style="font-size:7.5px;font-weight:700">SCRUBBER</text>
      <circle cx="1095" cy="230" r="32" fill="#10b981" fill-opacity="0.18" stroke="var(--hmi-stroke)" stroke-width="1.4"/>
      <path d="M 1095 208 L 1103 222 L 1087 222 Z" fill="#10b981" fill-opacity="0.75"/>
      <path d="M 1117 230 L 1103 238 L 1103 222 Z" fill="#10b981" fill-opacity="0.75"/>
      <path d="M 1095 252 L 1087 238 L 1103 238 Z" fill="#10b981" fill-opacity="0.75"/>
      <path d="M 1073 230 L 1087 222 L 1087 238 Z" fill="#10b981" fill-opacity="0.75"/>
      <circle cx="1095" cy="230" r="4" fill="var(--hmi-ground)" stroke="var(--hmi-stroke)" stroke-width="1"/>
      <text x="1095" y="277" text-anchor="middle" class="lbl-in" style="font-size:7.5px;font-weight:700">TRT</text>
      <path d="M 945 230 L 975 230" stroke="var(--hmi-stroke)" stroke-width="1.5" stroke-opacity="0.55" stroke-dasharray="3,3"/>
      <path d="M 1035 230 L 1063 230" stroke="var(--hmi-stroke)" stroke-width="1.5" stroke-opacity="0.55" stroke-dasharray="3,3"/>
    </g>
    <text x="1025" y="307" text-anchor="middle" class="lbl-in">TRT Power &#183; Calorific Value</text>
    <text x="1025" y="322" text-anchor="middle" class="lbl-in">Dust Load &#183; Flaring &#183; CO&#8322; Intensity</text>
  </g>

  <!-- ============ Clean gas stack (decoration, not clickable) ============ -->
  <g pointer-events="none">
    <path d="M 1235 380 L 1245 50 L 1295 50 L 1305 380 Z" fill="url(#bf2-stack)" stroke="var(--hmi-stroke-soft)" stroke-width="1"/>
    <ellipse cx="1270" cy="50" rx="25" ry="4" fill="var(--hmi-ground)" stroke="var(--hmi-stroke-soft)" stroke-width="0.5"/>
    <ellipse cx="1270" cy="40" rx="28" ry="7" fill="#a78bfa" fill-opacity="0.35"/>
    <ellipse cx="1273" cy="22" rx="34" ry="8" fill="#a78bfa" fill-opacity="0.25"/>
    <ellipse cx="1276" cy="6" rx="40" ry="10" fill="#a78bfa" fill-opacity="0.15"/>
    <text x="1270" y="398" text-anchor="middle" class="lbl-sm" style="font-weight:700">CLEAN GAS STACK</text>
  </g>

  <!-- ============ Flow legend (decoration) ============ -->
  <g transform="translate(50,680)" pointer-events="none">
    <text x="0" y="0" class="lbl-sm" style="font-weight:700;letter-spacing:0.5px">FLOWS:</text>
    <line x1="48" y1="-3" x2="66" y2="-3" stroke="var(--hmi-stroke)" stroke-width="3"/>
    <text x="69" y="0" class="lbl-sm">burden</text>
    <line x1="115" y1="-3" x2="133" y2="-3" stroke="#f59e0b" stroke-width="3"/>
    <text x="136" y="0" class="lbl-sm">hot blast</text>
    <line x1="186" y1="-3" x2="204" y2="-3" stroke="#b45309" stroke-width="3"/>
    <text x="207" y="0" class="lbl-sm">PCI coal</text>
    <line x1="252" y1="-3" x2="270" y2="-3" stroke="#dc2626" stroke-width="3"/>
    <text x="273" y="0" class="lbl-sm">hot metal</text>
    <line x1="328" y1="-3" x2="346" y2="-3" stroke="#a78bfa" stroke-width="3"/>
    <text x="349" y="0" class="lbl-sm">slag</text>
    <line x1="384" y1="-3" x2="402" y2="-3" stroke="#7c3aed" stroke-width="3"/>
    <text x="405" y="0" class="lbl-sm">top gas</text>
    <line x1="455" y1="-3" x2="473" y2="-3" stroke="#06b6d4" stroke-width="3"/>
    <text x="476" y="0" class="lbl-sm">clean BF gas</text>
    <line x1="546" y1="-3" x2="564" y2="-3" stroke="#10b981" stroke-width="3"/>
    <text x="567" y="0" class="lbl-sm">TRT power</text>
  </g>
</svg>`;

export const BLASTFURNACE_V2_TEMPLATE = {
  id: BLASTFURNACE_V2_TEMPLATE_ID,
  name: 'Blast Furnace',
  svgUrl: '/svg-templates/blastfurnace_v2.svg',
  sections: BLASTFURNACE_V2_SECTIONS,
};

/** Default catalog used when BE returns no templates. v2 (detailed) is the default. */
export const FALLBACK_TEMPLATES = [BLASTFURNACE_V2_TEMPLATE, BLASTFURNACE_TEMPLATE];

/** Id of the bundled default template (first shown / pre-selected). */
export const DEFAULT_TEMPLATE_ID = BLASTFURNACE_V2_TEMPLATE_ID;
